import { AttributeDefinitionDc, FeatureDc, LayerDefinitionDc, StringSubType } from "@evergis/api";

import { getExpressionAttributeName } from "../../../components/SymbolEditor/hooks/useExpressionAttribute";
import { isSelectedFeature } from "../../../ducks/selectFeatures/utils";
import { ClassificationParameter } from "../../../interfaces/classification";
import { LayerInfo } from "../../../interfaces/resourceInfo";
import { StyleSymbolType } from "../../../interfaces/style";
import { FeatureAttribute } from "../components/Attributes/FeatureAttributeField";
import { SelectedFeatureDc } from "../../../ducks/selectFeatures/types";
import { FeatureDataSource } from "../../../ducks/showFeature/types";

function getLayerAttributes(layerDefinition: LayerDefinitionDc): [string, AttributeDefinitionDc][] {
  const { attributes, geometryAttribute } = layerDefinition || {};

  return Object.entries(attributes ?? {}).filter(
    ([attributeName, { isDisplayed }]) => ![geometryAttribute].includes(attributeName) && isDisplayed,
  );
}

export const getCalculatedAttributes = (
  symbol: StyleSymbolType | undefined,
): Record<string, ClassificationParameter> => {
  return Object.entries(symbol ?? {}).reduce((acc, [parameterName, parameterValue]) => {
    const attributeName = getExpressionAttributeName(parameterValue);

    if (attributeName) {
      acc[attributeName] = parameterName;
    }

    return acc;
  }, {});
};

export function getFeatureAttributes(
  feature: FeatureDc | SelectedFeatureDc = {} as FeatureDc,
  layer?: LayerInfo,
  dataSource?: FeatureDataSource,
): FeatureAttribute[] {
  const layerDefinition = isSelectedFeature(feature) ? feature.layerDefinition : layer?.layerDefinition;

  if (!layerDefinition) {
    return [];
  }

  const currentAttributes = !feature && dataSource ? dataSource.features[0].attributes : feature?.attributes;
  const { id: idValue } = feature || {};
  const { idAttribute, attributes } = layerDefinition || {};
  const layerAttributes = idAttribute
    ? getLayerAttributes(layerDefinition)
    : attributes
    ? Object.entries(attributes)
    : [];
  const calculatedAttributes = getCalculatedAttributes(layer?.style?.symbol);

  // TODO: fix typings for "id" field, DO NOT REMOVE "id", it uses in src\hooks\attributes\useAttributes.ts
  return layerAttributes.map(
    ([
      attributeName,
      { type, subType, isCalculated, isEditable, stringFormat, alias = null, description },
    ]): FeatureAttribute & {
      id: string;
      subType?: StringSubType;
    } => {
      const isIdAttribute = attributeName === idAttribute;

      const additionalProps = isIdAttribute
        ? {
            value: idValue,
            readOnly: true,
          }
        : {
            value: currentAttributes?.[attributeName] || dataSource?.features?.[0]?.attributes?.[attributeName],
            readOnly: isCalculated || !isEditable,
          };

      const clientData = layer.layerAttributes?.find(
        layerAttribute => layerAttribute.attributeName === attributeName,
      )?.clientData;

      return {
        id: attributeName,
        name: attributeName,
        alias,
        type,
        subType,
        stringFormat,
        calculatedParameter: calculatedAttributes[attributeName] || null,
        description,
        clientData,
        ...additionalProps,
      };
    },
  );
}
